import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_MOCK, rulesApi } from '@/api/client';
import { DEFAULT_CONFIG } from '@/views/AdminView/tabs/RulesTab/constants';
import type { OUNode } from '@/data/mockOUs';
import type { RuleItem } from '@/api/types';
import { useOUs } from './useOUs';

/** Shape returned by the hook for convenient access in RulesTab */
export interface RulesConfig {
  rn001: typeof DEFAULT_CONFIG.rn001 & { enabled: boolean };
  rn002: { exclRecur: boolean; exclWork: boolean; keywords: string[]; enabled: boolean };
  rn003: typeof DEFAULT_CONFIG.rn003 & { enabled: boolean };
  rn004: typeof DEFAULT_CONFIG.rn004 & { enabled: boolean };
  rn005: typeof DEFAULT_CONFIG.rn005 & { enabled: boolean };
  ouAdminsCanCreate: boolean;
  /** RN-000: active state per OU, keyed by OU id */
  ouActive: Record<string, boolean>;
  /** Per-OU overrides keyed by OU id — only entries that differ from global */
  rn001OuOverrides: Record<string, Record<string, number>>;
  rn002OuOverrides: Record<string, { recurr: boolean; work: boolean; keywords: string[] }>;
  rn004OuOverrides: Record<string, Record<string, number>>;
  rn005OuOverrides: Record<string, Record<string, number>>;
}

function itemsToConfig(items: RuleItem[], ous: OUNode[]): RulesConfig {
  function get(code: string) { return items.find(r => r.code === code); }
  function numVal(code: string, fallback: number): number {
    const item = get(code);
    if (!item) return fallback;
    const n = Number(item.value);
    return Number.isFinite(n) ? n : fallback;
  }
  function boolVal(code: string, fallback: boolean): boolean {
    const item = get(code);
    if (!item) return fallback;
    return item.value === 'true';
  }
  function strVal(code: string, fallback: string): string {
    return get(code)?.value ?? fallback;
  }

  const minDur   = numVal('MEETING_MIN_DURATION_MINUTES', DEFAULT_CONFIG.rn001.minDur);
  const maxDur   = numVal('MEETING_MAX_DURATION_MINUTES', DEFAULT_CONFIG.rn001.maxDur);
  const minPax   = numVal('MEETING_MIN_PARTICIPANTS',     DEFAULT_CONFIG.rn001.minPax);
  const maxPax   = numVal('MEETING_MAX_PARTICIPANTS',     DEFAULT_CONFIG.rn001.maxPax);
  const maxWeek  = numVal('MAX_EVALUATIONS_PER_WEEK',     DEFAULT_CONFIG.rn004.maxWeek);
  const maxPer   = numVal('T5_EVALUATION_COUNT',          DEFAULT_CONFIG.rn004.maxPer);
  const interval = numVal('SURVEY_MIN_INTERVAL_HOURS',    DEFAULT_CONFIG.rn004.interval);
  const maxDay   = numVal('MAX_EVALUATIONS_PER_DAY',      DEFAULT_CONFIG.rn005.maxDay);

  const exclRecur   = boolVal('EXCLUDE_RECURRING_MEETINGS', true);
  const exclWork    = boolVal('EXCLUDE_OUTSIDE_WORK_HOURS', true);
  const kwRaw       = strVal('EXCLUDE_KEYWORDS', '');
  const keywords    = kwRaw ? kwRaw.split(',').map(k => k.trim()).filter(Boolean) : [];
  const workFrom  = strVal('WORK_HOURS_START', DEFAULT_CONFIG.rn003.from);
  const workTo    = strVal('WORK_HOURS_END',   DEFAULT_CONFIG.rn003.to);
  const ouAdminsCanCreate = boolVal('OU_ADMINS_CREATE_SURVEYS', false);

  // Use isActive from any of the group rules as the "enabled" flag
  const rn001Active = get('MEETING_MIN_DURATION_MINUTES')?.isActive ?? DEFAULT_CONFIG.rn001Enabled;
  const rn002Active = get('EXCLUDE_RECURRING_MEETINGS')?.isActive   ?? DEFAULT_CONFIG.rn002Enabled;
  const rn003Active = get('WORK_HOURS_START')?.isActive             ?? DEFAULT_CONFIG.rn003Enabled;
  const rn004Active = get('MAX_EVALUATIONS_PER_WEEK')?.isActive     ?? DEFAULT_CONFIG.rn004Enabled;
  const rn005Active = get('MAX_EVALUATIONS_PER_DAY')?.isActive      ?? DEFAULT_CONFIG.rn005Enabled;

  // RN-000: OU active states — código canónico OU_ACTIVE, override por ouId (no más slug embebido en el código)
  const ouActiveItem = get('OU_ACTIVE');
  const ouActiveGlobalDefault = ouActiveItem ? ouActiveItem.value === 'true' : true;
  const ouActive: Record<string, boolean> = {};
  ous.forEach(ou => {
    const override = ouActiveItem?.ouOverrides?.find(o => o.ouId === ou.key)?.value;
    ouActive[ou.key] = override !== undefined ? override === 'true' : (ou.active ?? ouActiveGlobalDefault);
  });

  const rn001OuOverrides: Record<string, Record<string, number>> = {};
  const rn002OuOverrides: Record<string, { recurr: boolean; work: boolean; keywords: string[] }> = {};
  const rn004OuOverrides: Record<string, Record<string, number>> = {};
  const rn005OuOverrides: Record<string, Record<string, number>> = {};

  ous.forEach(ou => {
    const ov = (code: string) =>
      get(code)?.ouOverrides?.find(o => o.ouId === ou.key)?.value;

    const m1 = ov('MEETING_MIN_DURATION_MINUTES');
    const m2 = ov('MEETING_MAX_DURATION_MINUTES');
    const m3 = ov('MEETING_MIN_PARTICIPANTS');
    const m4 = ov('MEETING_MAX_PARTICIPANTS');
    if (m1 !== undefined || m2 !== undefined || m3 !== undefined || m4 !== undefined) {
      rn001OuOverrides[ou.key] = {
        minDur: m1 !== undefined ? Number(m1) : minDur,
        maxDur: m2 !== undefined ? Number(m2) : maxDur,
        minPax: m3 !== undefined ? Number(m3) : minPax,
        maxPax: m4 !== undefined ? Number(m4) : maxPax,
      };
    }

    const r1 = ov('EXCLUDE_RECURRING_MEETINGS');
    const r2 = ov('EXCLUDE_OUTSIDE_WORK_HOURS');
    const r3 = ov('EXCLUDE_KEYWORDS');
    if (r1 !== undefined || r2 !== undefined || r3 !== undefined) {
      rn002OuOverrides[ou.key] = {
        recurr:   r1 !== undefined ? r1 === 'true' : exclRecur,
        work:     r2 !== undefined ? r2 === 'true' : exclWork,
        keywords: r3 !== undefined && r3.length > 0 ? r3.split(',').map(k => k.trim()).filter(Boolean) : [],
      };
    }

    const w1 = ov('MAX_EVALUATIONS_PER_WEEK');
    const w2 = ov('T5_EVALUATION_COUNT');
    const w3 = ov('SURVEY_MIN_INTERVAL_HOURS');
    if (w1 !== undefined || w2 !== undefined || w3 !== undefined) {
      rn004OuOverrides[ou.key] = {
        maxWeek:  w1 !== undefined ? Number(w1) : maxWeek,
        maxPer:   w2 !== undefined ? Number(w2) : maxPer,
        interval: w3 !== undefined ? Number(w3) : interval,
      };
    }

    const d1 = ov('MAX_EVALUATIONS_PER_DAY');
    if (d1 !== undefined) {
      rn005OuOverrides[ou.key] = { maxDay: Number(d1) };
    }
  });

  return {
    rn001: { minDur, maxDur, minPax, maxPax, enabled: rn001Active },
    rn002: { exclRecur, exclWork, keywords, enabled: rn002Active },
    rn003: { from: workFrom, to: workTo, enabled: rn003Active },
    rn004: { maxWeek, maxPer, interval, enabled: rn004Active },
    rn005: { maxDay, enabled: rn005Active },
    ouAdminsCanCreate,
    ouActive,
    rn001OuOverrides,
    rn002OuOverrides,
    rn004OuOverrides,
    rn005OuOverrides,
  };
}

export function useRules() {
  const { data: ous = [] } = useOUs();

  return useQuery<RulesConfig>({
    queryKey: ['rules', ous.map(o => o.key).join(',')],
    queryFn: async () => {
      if (USE_MOCK) {
        const ouActive: Record<string, boolean> = {};
        ous.forEach(ou => { ouActive[ou.key] = ou.active ?? true; });
        return {
          rn001: { ...DEFAULT_CONFIG.rn001, enabled: DEFAULT_CONFIG.rn001Enabled },
          rn002: { exclRecur: true, exclWork: true, keywords: [], enabled: DEFAULT_CONFIG.rn002Enabled },
          rn003: { ...DEFAULT_CONFIG.rn003, enabled: DEFAULT_CONFIG.rn003Enabled },
          rn004: { ...DEFAULT_CONFIG.rn004, enabled: DEFAULT_CONFIG.rn004Enabled },
          rn005: { ...DEFAULT_CONFIG.rn005, enabled: DEFAULT_CONFIG.rn005Enabled },
          ouAdminsCanCreate: false,
          ouActive,
          rn001OuOverrides: {},
          rn002OuOverrides: {},
          rn004OuOverrides: {},
          rn005OuOverrides: {},
        };
      }
      const data = await rulesApi.getRules();
      return itemsToConfig(data, ous);
    },
  });
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, body }: { code: string; body: Omit<RuleItem, 'code'> }) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] updateRule', code, body);
        return Promise.resolve({ code, ...body });
      }
      return rulesApi.updateRule(code, body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['rules'] }); },
  });
}

export function useUpsertRuleOuOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, ouId, value }: { code: string; ouId: string; value: string | null }) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] upsertRuleOuOverride', code, ouId, value);
        return Promise.resolve({ code, description: '', value: value ?? '', isActive: true });
      }
      return rulesApi.upsertRuleOuOverride(code, ouId, value);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['rules'] }); },
  });
}
